/* eslint-disable */
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { OrganisationHttpService } from 'libs/share-page/domain/src';
import { filter, map, switchMap, tap } from 'rxjs';
import { tapAllResponses } from 'shared/util-common';

import { ResourceTag } from '@treetop/shared/model';

import { ResourceTagChipComponent } from '../resource-tag-chip/resource-tag-chip.component';
import { randomHexColor } from '../tt-resource-tags.util';
import { ButtonDirective } from 'libs/shared/ui-common/src/lib/components/button/button.directive';
import { CheckboxComponent } from 'libs/shared/ui-common/src/lib/components/checkbox/checkbox.component';
import { FormFieldComponent } from 'libs/shared/ui-common/src/lib/components/form-field/form-field.component';
import { InputDirective } from 'libs/shared/ui-common/src/lib/components/input/input.directive';
import { DotComponent } from 'libs/shared/ui-common/src/lib/components/dot/dot.component';
import { SpinnerComponent } from 'libs/shared/ui-common/src/lib/components/spinner/spinner.component';
import { MessagesService } from 'libs/shared/domain/src/lib/infrastructure/messages.service';
import { create_UUID } from 'libs/shared/domain/src/lib/infrastructure/FormResolver';

@Component({
  selector: 'tt-add-resource-tags',
  standalone: true,
  templateUrl: './add-resource-tags.component.html',
  styleUrls: ['./add-resource-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-testid': 'tt-add-resource-tags',
  },
  imports: [
    FormsModule,
    ButtonDirective,
    CheckboxComponent,
    FormFieldComponent,
    InputDirective,
    DotComponent,
    SpinnerComponent,
    TranslocoModule,
    ResourceTagChipComponent,
  ],
})
export class AddResourceTagsComponent {
  private readonly organisationHttpService = inject(OrganisationHttpService);
  private messagesService = inject(MessagesService);

  public selectedTags = input<ResourceTag[]>([]);
  public tagsChanged = output<ResourceTag[]>();

  protected searchTerm = signal<string>('');
  protected loading = signal<boolean>(false);
  protected tags = signal<ResourceTag[]>([]);

  protected readonly selectedColor = signal<string>(randomHexColor());
  protected readonly selectedBackgroundColor = signal<string>(randomHexColor());

  private _wasShowingAddOption = false;

  protected readonly filteredTags = computed(() => {
    const query = this.searchTerm().toLowerCase().trim();
    const selectedIds = new Set(this.selectedTags().map((t) => t.id));
    const tags = query ? this.tags().filter((tag) => tag.label.toLowerCase().includes(query)) : this.tags();
    return tags.map((tag) => ({ ...tag, selected: selectedIds.has(tag.id) }));
  });

  protected readonly showAddOption = computed(() => {
    const query = this.searchTerm().toLowerCase().trim();
    if (!query) {
      return false;
    }

    return !this.tags().some((tag) => tag.label.toLowerCase() === query.toLowerCase());
  });

  constructor() {
    this.loadTags();

    effect(() => {
      const showing = this.showAddOption();
      if (showing && !this._wasShowingAddOption) {
        this.selectedColor.set(randomHexColor());
        this.selectedBackgroundColor.set(randomHexColor());
      }
      this._wasShowingAddOption = showing;
    });
  }

  private readonly loadTags = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      tap(() => this.loading.set(true)),
      switchMap(() => this.organisationHttpService.getOrganisationResourceTags()),
      tapAllResponses<ResourceTag[]>(
        (tags) => {
          if (tags?.length) {
            this.tags.set(tags);
          }
          this.loading.set(false);
        },
        (error) => {
          this.loading.set(false);
          this.messagesService.showError('ResourceTag.ErrorLoadingTags', error);
        },
      ),
    ),
  );

  protected makeAddNewTagCall = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      map(() => this.searchTerm().trim()),
      filter((label) => !!label),
      tap(() => this.searchTerm.set('')),
      map((label) => ({ id: create_UUID(), label, color: this.selectedColor(), backgroundColor: this.selectedBackgroundColor() })),
      switchMap((tag) => this.organisationHttpService.addOrganisationResourceTag(tag)),
      tapAllResponses<ResourceTag>(
        (newTag) => {
          this.tags.update((tags) => [...tags, newTag]);
          this.toggleTag(null, newTag);
        },
        (error) => {
          this.messagesService.showError('ResourceTag.ErrorAddingNewTag', error);
        },
      ),
    ),
  );

  protected toggleTag(event: MouseEvent | null, tag: ResourceTag): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.selectedTags()?.some((t) => t.id === tag.id)) {
      this.tagsChanged.emit(this.selectedTags().filter((t) => t.id !== tag.id));
    } else {
      this.tagsChanged.emit([...this.selectedTags(), tag]);
    }
  }
}
